import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { CasesTableParams } from '..'
import { debounceAsync } from '@/utils/debounce'
import { DataTableFacetedFilter } from '@/views/users/components/data-table-faceted-filter'
import { useCaseTypes } from '@/hooks/use-case-types'
import { CaseType } from '../data/schema'
import { useState, useRef, useEffect } from 'react'
import { t } from 'i18next'
import { PaginatedSelect } from '@/components/paginated-select'
import { get_clients } from '@/api/client'
import { get_users } from '@/api/user'
import { X, Filter, Search, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTableViewOptions } from '@/components/data-table/data-table-view-options'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface DataTableToolbarProps<TData> {
  table: Table<TData>
  params: CasesTableParams
  setParams: React.Dispatch<React.SetStateAction<CasesTableParams>>
}

export function DataTableToolbar<TData>({
  table,
  params,
  setParams,
}: DataTableToolbarProps<TData>) {
  const i18n = useTranslation()
  const { caseTypes, isLoading: caseTypesLoading } = useCaseTypes()

  const [searchMode, setSearchMode] = useState<'name' | 'case_id'>(
    params.name !== null ? 'name' : 'case_id'
  )
  const [caseIdYear, setCaseIdYear] = useState(params.case_id?.split('/')[0] || '')
  const [caseIdNumber, setCaseIdNumber] = useState(params.case_id?.split('/')[2] || '')
  const [caseIdCode, setCaseIdCode] = useState(params.case_id?.split('/')[1] || '')
  const [selectedClient, setSelectedClient] = useState<{ id: string | number; name: string } | null>(null)
  const [selectedUser, setSelectedUser] = useState<{ id: string | number; name: string } | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const activeFiltersCount = [
    params.types && params.types.length > 0,
    params.status && params.status.length > 0,
    params.client,
    params.assigned_to,
    params.is_favorite,
  ].filter(Boolean).length

  // Debounced text search ----------------------------------------------------
  const debouncedSearchRef = useRef<(value: string, mode: 'name' | 'case_id') => Promise<void>>(null!)

  useEffect(() => {
    debouncedSearchRef.current = debounceAsync(async (value: string, mode: 'name' | 'case_id') => {
      if (mode === 'name') {
        setParams((prev) => ({ ...prev, name: value === '' ? null : value, case_id: null }))
      } else {
        setParams((prev) => ({ ...prev, case_id: value === '' ? null : value, name: null }))
      }
    }, 500)
  }, [setParams])

  // Hydrate selected client/user from existing query params ------------------
  useEffect(() => {
    const initializeFilters = async () => {
      if (params.client && !selectedClient) {
        try {
          const r = await get_clients({ page: 0, page_size: 20, ordering: null, name: null })
          const client = r.results.find((c) => c.id === params.client)
          if (client) setSelectedClient({ id: client.id, name: client.name })
        } catch (e) {
          console.error('Failed to fetch client:', e)
        }
      }
      if (params.assigned_to && !selectedUser) {
        try {
          const r = await get_users({
            page: 0, page_size: 20, ordering: null, name: null, roles: null, is_active: null,
          })
          const user = r.results.find((u) => u.id === params.assigned_to)
          if (user) setSelectedUser({ id: user.id, name: user.name })
        } catch (e) {
          console.error('Failed to fetch user:', e)
        }
      }
    }
    initializeFilters()
  }, [params.client, params.assigned_to, selectedClient, selectedUser])

  useEffect(() => {
    if (!params.client && selectedClient) setSelectedClient(null)
    if (!params.assigned_to && selectedUser) setSelectedUser(null)
  }, [params.client, params.assigned_to, selectedClient, selectedUser])

  // Handlers -----------------------------------------------------------------
  const handleNameSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearchRef.current?.(event.target.value, 'name')
  }

  const handleCaseIdPartChange = (part: 'year' | 'number' | 'code', value: string) => {
    if (part === 'year') setCaseIdYear(value)
    else if (part === 'code') setCaseIdCode(value)
    else if (part === 'number') setCaseIdNumber(value)

    const newYear = part === 'year' ? value : caseIdYear
    const newCode = part === 'code' ? value : caseIdCode
    const newNumber = part === 'number' ? value : caseIdNumber

    if ((newYear && newYear.length !== 4) || (newCode && newCode.length !== 4)) return

    let full = [newYear, newCode, newNumber].join('/')
    if (full === '//') full = ''
    debouncedSearchRef.current?.(full, 'case_id')
  }

  const handleTypeFacetChange = (values: string[]) => {
    setParams({ ...params, types: values })
  }

  const handleClientChange = (client: { id: string | number; name: string } | null) => {
    setSelectedClient(client)
    setParams({ ...params, client: client?.id.toString() || null })
  }

  const handleAssignedToChange = (user: { id: string | number; name: string } | null) => {
    setSelectedUser(user)
    setParams({ ...params, assigned_to: user?.id.toString() || null })
  }

  const resetAllFilters = () => {
    setSelectedClient(null)
    setSelectedUser(null)
    setParams({
      ...params,
      types: null,
      status: null,
      client: null,
      assigned_to: null,
      is_favorite: null,
    })
  }

  const hasCaseIdValues = !!(caseIdYear || caseIdCode || caseIdNumber)
  const isSearchActive = !!params.name || !!params.case_id

  return (
    <div className="space-y-3">
      {/* ----- Main Toolbar Row ------------------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Search Section */}
        <div
          dir="ltr"
          className={cn(
            'flex items-center w-full sm:w-auto sm:flex-1 sm:max-w-xl',
            'shadow-sm rounded-lg overflow-hidden border transition-colors',
            isSearchActive
              ? 'border-primary/50 ring-1 ring-primary/10'
              : 'border-input hover:border-muted-foreground/30 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
          )}
        >
          {/* Mode selector */}
          <div className="relative">
            <select
              className={cn(
                'h-9 appearance-none rounded-none border-0 border-e border-input bg-muted/40 pe-7 ps-3',
                'text-sm font-medium text-foreground/80 outline-none transition-colors',
                'hover:bg-muted focus:bg-muted'
              )}
              value={searchMode}
              onChange={(e) => {
                const newMode = e.target.value as 'name' | 'case_id'
                setSearchMode(newMode)
                if (newMode === 'name' && params.case_id) {
                  setCaseIdCode(''); setCaseIdYear(''); setCaseIdNumber('')
                  setParams({ ...params, case_id: null, name: null })
                } else if (newMode === 'case_id' && params.name) {
                  setParams({ ...params, name: null, case_id: null })
                }
              }}
            >
              <option value="name">{i18n.t('search_by_case_name')}</option>
              <option value="case_id">{i18n.t('search_by_case_id') || 'Search by Case ID'}</option>
            </select>
            <ChevronDown className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          </div>

          {/* Search input(s) */}
          {searchMode === 'name' ? (
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                dir="rtl"
                type="text"
                placeholder={i18n.t('search')}
                onChange={handleNameSearchChange}
                defaultValue={params.name || ''}
                className="h-9 w-full border-0 bg-transparent ps-9 pe-3 text-sm outline-none placeholder:text-muted-foreground/70"
              />
            </div>
          ) : (
            <div className="flex items-center h-9 flex-1 px-2 gap-1">
              <input
                type="text"
                inputMode="numeric"
                placeholder={t('year')}
                defaultValue={params.case_id?.split('/')[0] || ''}
                onChange={(e) => handleCaseIdPartChange('year', e.target.value)}
                className={cn(
                  'h-7 w-12 text-center text-sm rounded-md outline-none bg-transparent',
                  'placeholder:text-muted-foreground/60',
                  'focus:bg-primary/5 transition-colors',
                  caseIdYear && 'font-medium text-foreground'
                )}
                maxLength={4}
              />
              <span className="text-muted-foreground/60 select-none">/</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder={t('code')}
                defaultValue={params.case_id?.split('/')[1] || ''}
                onChange={(e) => handleCaseIdPartChange('code', e.target.value)}
                className={cn(
                  'h-7 w-12 text-center text-sm rounded-md outline-none bg-transparent',
                  'placeholder:text-muted-foreground/60',
                  'focus:bg-primary/5 transition-colors',
                  caseIdCode && 'font-medium text-foreground'
                )}
                maxLength={4}
              />
              <span className="text-muted-foreground/60 select-none">/</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder={t('number')}
                defaultValue={params.case_id?.split('/')[2] || ''}
                onChange={(e) => handleCaseIdPartChange('number', e.target.value)}
                className={cn(
                  'h-7 w-16 text-center text-sm rounded-md outline-none bg-transparent',
                  'placeholder:text-muted-foreground/60',
                  'focus:bg-primary/5 transition-colors',
                  caseIdNumber && 'font-medium text-foreground'
                )}
                maxLength={6}
              />
              {hasCaseIdValues && (
                <button
                  type="button"
                  onClick={() => {
                    setCaseIdCode(''); setCaseIdYear(''); setCaseIdNumber('')
                    setParams({ ...params, case_id: null })
                  }}
                  className="ms-1 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  aria-label={i18n.t('clear')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <Button
            variant={showFilters || activeFiltersCount > 0 ? 'secondary' : 'outline'}
            size="sm"
            className="h-9 gap-2"
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            <span>{i18n.t('filters')}</span>
            {activeFiltersCount > 0 && (
              <Badge
                variant="secondary"
                className="ms-1 rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center text-[10px] font-semibold bg-primary text-primary-foreground"
              >
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
          <DataTableViewOptions table={table} />
        </div>
      </div>

      {/* ----- Collapsible Filters Section -------------------------------- */}
      {showFilters && (
        <div className="relative rounded-lg border border-border/60 bg-muted/30 p-3 animate-in slide-in-from-top-2 duration-200">
          {/* Header row inside the panel */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {i18n.t('filters')}
              {activeFiltersCount > 0 && (
                <span className="ms-2 text-primary font-medium normal-case">
                  ({activeFiltersCount} {i18n.t('active') || 'active'})
                </span>
              )}
            </p>
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1 text-muted-foreground hover:text-destructive"
                onClick={resetAllFilters}
              >
                <X className="h-3.5 w-3.5" />
                {i18n.t('clear_filters') || i18n.t('clear')}
              </Button>
            )}
          </div>

          {/* Filter controls grid */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Facet pills */}
            {table.getColumn('case_type') && !caseTypesLoading && (
              <DataTableFacetedFilter
                title={i18n.t('case_type')}
                options={caseTypes.map((t: CaseType) => ({ label: t.name, value: t.id.toString() }))}
                defaultSelected={params.types || []}
                onFacetChange={handleTypeFacetChange}
                triggerId="filter-type"
              />
            )}
            <DataTableFacetedFilter
              title={i18n.t('case_status')}
              options={[
                { label: i18n.t('case_status.active'), value: 'active' },
                { label: i18n.t('case_status.audience'), value: 'audience' },
                { label: i18n.t('case_status.delibere'), value: 'delibere' },
                { label: i18n.t('case_status.suspended'), value: 'suspended' },
                { label: i18n.t('case_status.closed'), value: 'closed' },
                { label: i18n.t('case_status.archived'), value: 'archived' },
              ]}
              defaultSelected={params.status || []}
              onFacetChange={(values) => {
                setParams({ ...params, status: values.length > 0 ? values : null })
              }}
              triggerId="filter-status"
            />
            <DataTableFacetedFilter
              title={i18n.t('priority')}
              options={[{ label: i18n.t('only_priority'), value: 'true' }]}
              defaultSelected={params.is_favorite ? ['true'] : []}
              onFacetChange={(values) => {
                setParams({ ...params, is_favorite: values.includes('true') ? true : null })
              }}
              triggerId="filter-priority"
            />

            <div className="h-6 w-px bg-border" />

            {/* Paginated dropdowns */}
            <div className="flex items-center gap-1">
              <PaginatedSelect
                placeholder={i18n.t('select_client')}
                triggerId="client-filter-trigger"
                fetchItems={async (page, search) => {
                  const result = await get_clients({
                    page: (page - 1) * 20,
                    page_size: 20,
                    ordering: null,
                    name: search || null,
                  })
                  return {
                    items: result.results.map((c) => ({ id: c.id, name: c.name })),
                    hasMore: result.next !== null,
                  }
                }}
                onSelect={handleClientChange}
                value={selectedClient}
              />
              {selectedClient && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                  onClick={() => handleClientChange(null)}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">{i18n.t('clear')}</span>
                </Button>
              )}
            </div>

            <div className="flex items-center gap-1">
              <PaginatedSelect
                placeholder={i18n.t('select_assigned_to')}
                triggerId="assigned-to-filter-trigger"
                fetchItems={async (page, search) => {
                  const result = await get_users({
                    page: (page - 1) * 20,
                    page_size: 20,
                    ordering: null,
                    name: search || null,
                    roles: null,
                    is_active: null,
                  })
                  return {
                    items: result.results.map((u) => ({ id: u.id, name: u.name })),
                    hasMore: result.next !== null,
                  }
                }}
                onSelect={handleAssignedToChange}
                value={selectedUser}
              />
              {selectedUser && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                  onClick={() => handleAssignedToChange(null)}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">{i18n.t('clear')}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
